import {
  Button,
  Card,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { backlogStatusOptions, getBacklogStatus, getPrettyBacklogStatus } from '@pelicargo/utils';
import { BacklogStatus } from '@prisma/client';
import { addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HiCheck, HiOutlinePlusCircle } from 'react-icons/hi2';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { Quote } from '../../utils/types';
import { LabeledText } from '../LabeledText';
import { SimpleForm } from '../SimpleForm';
import { SimpleNativeDatePicker } from '../SimpleNativeDatePicker';
import { SimplePopoverInput } from '../SimplePopoverInput';
import { SimpleSelect } from '../SimpleSelect';

const IndividualBacklog = ({
  backlog,
  refetch,
}: {
  backlog: TrpcOutputs['isQuoteBacklogged']['backlogs'][0];
  refetch: () => Promise<any>;
}) => {
  const [isResolving, setIsResolving] = useState(false);
  const { mutateAsync: upsertBacklog } = trpc.upsertBacklog.useMutation();
  const methods = useForm<Extract<Parameters<typeof upsertBacklog>[0], {}>>({ defaultValues: backlog });
  const { isOpen, onToggle } = useDisclosure();
  const onSubmit = async () => {
    const values = methods.getValues();
    await upsertBacklog(values);
    refetch();
  };

  const resolveBacklog = async () => {
    try {
      setIsResolving(true);
      await upsertBacklog({ ...backlog, expires_at: new Date() });
      refetch();
    } catch (error) {
      console.error('Error resolving backlog:', error);
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <Card p="2" bg="none" minW="300px">
      <Stack gap="4">
        <HStack justifyContent="space-between">
          <Text fontWeight="bold" fontSize="md">
            {getPrettyBacklogStatus(backlog.status)}
          </Text>
          <Button onClick={onToggle} colorScheme={isOpen ? 'primary' : undefined} size="xs">
            {isOpen ? 'Done' : 'Modify'}
          </Button>
        </HStack>
        <Stack>
          <HStack justifyContent="space-between" fontSize="md">
            <Text>Active Dates</Text>
            <Text as="span">
              {backlog?.starts_at ? formatInTimeZone(backlog?.starts_at, 'Etc/UTC', 'MM/dd/yyyy') : '-'} to{' '}
              {backlog?.expires_at ? formatInTimeZone(backlog?.expires_at, 'Etc/UTC', 'MM/dd/yyyy') : 'forever'}
            </Text>
          </HStack>
          <HStack justifyContent="space-between" fontSize="md">
            <Text>Next Flight</Text>
            <Text>
              {backlog?.next_flight_at
                ? formatInTimeZone(backlog?.next_flight_at, 'Etc/UTC', 'MM/dd/yyyy')
                : 'Not Specified'}
            </Text>
          </HStack>
        </Stack>
        <Show if={isOpen}>
          <SimpleForm {...methods} defaultValues={backlog} onValid={onSubmit}>
            <Stack as="form">
              <SimpleSelect name="status" label="Status" options={backlogStatusOptions} />
              <SimpleNativeDatePicker name="expires_at" label="Expires At" />
              <Show if={backlog?.status !== BacklogStatus.AtRisk}>
                <SimpleNativeDatePicker name="next_flight_at" label="Next Flight At" />
              </Show>
              <HStack justifyContent="space-between">
                <Text fontSize="md">ID# {backlog.id}</Text>
                <Button size="xs" leftIcon={<HiCheck />} onClick={resolveBacklog} isLoading={isResolving}>
                  Resolve Backlog
                </Button>
              </HStack>
            </Stack>
          </SimpleForm>
        </Show>
      </Stack>
    </Card>
  );
};

export const BacklogEditor = ({ quote }: { quote: Quote }) => {
  const { data, refetch } = trpc.isQuoteBacklogged.useQuery({ id: quote.id });
  const { mutateAsync: upsertBacklog } = trpc.upsertBacklog.useMutation();

  const airports = useMemo(() => {
    return Array.from(
      quote?.flight_path?.reduce((agg, fp) => {
        agg.add(fp?.origin_airport_id);
        agg.add(fp?.destination_airport_id);
        return agg;
      }, new Set<string>()) ?? [],
    );
  }, [quote?.flight_path]);
  const [backlogData, setBacklogData] = useState(data);
  const [backlogsForAirportsLoading, setBacklogsForAirportsLoading] = useState(
    Object.fromEntries(airports.map((a) => [a, false])),
  );

  useEffect(() => {
    if (data?.backlogs?.length) setBacklogData(data);
  }, [data, data?.backlogs?.length]);

  trpc.onBacklogUpdate.useSubscription(
    { provider_id: quote.provider_id, airports: airports?.filter(Boolean) },
    {
      // Extremely simplistic, but since this can change state, we just re-grab everything
      onData: async (data) => {
        setBacklogData((prev) => {
          const nextBacklogs =
            prev?.backlogs
              ?.map((b) => {
                const isUpdated = b.id === data.id;
                const isExpired = isUpdated && data.expires_at && new Date(data.expires_at) < new Date();
                if (isExpired) {
                  return null;
                }
                return isUpdated ? { ...b, ...data } : b;
              })
              ?.filter(Boolean) || [];
          return {
            backlogs: nextBacklogs,
            backlogged: nextBacklogs.filter((b) => b.status === BacklogStatus.Backlogged),
            atRisk: nextBacklogs.filter((b) => b.status === BacklogStatus.AtRisk),
          };
        });
      },
    },
  );

  const display = useMemo(() => {
    const backlogs = backlogData?.backlogs;
    const maybeBacklog = backlogs?.find((b) => b.status === BacklogStatus.Backlogged);
    const maybeAtRisk = backlogs?.find((b) => b.status === BacklogStatus.AtRisk);
    const prioritizedBacklog = maybeBacklog || maybeAtRisk;

    const untilDateFormatted = prioritizedBacklog?.expires_at
      ? formatInTimeZone(prioritizedBacklog?.expires_at, 'Etc/UTC', 'MM/dd/yyyy')
      : '-';

    if (!backlogs?.length) return 'None';
    if (backlogs.length > 1) {
      if (prioritizedBacklog) {
        const prettyAirports = backlogs.map((b) => b.airport_id).join(', ');
        return `${prettyAirports} Multiple Backlogs`;
      }
    }

    const backloggedAirports = backlogs.map((x) => x.airport_id);
    const response = `${backloggedAirports.join(', ')} ` + (maybeBacklog ? 'backlogged' : 'at risk');
    if (maybeBacklog?.expires_at) {
      return response + ` until ${untilDateFormatted}`;
    }
    return response;
  }, [backlogData?.backlogs]);

  const addBacklog = useCallback(
    async (airport_id: string, status: BacklogStatus) => {
      try {
        setBacklogsForAirportsLoading((prev) => ({ ...prev, [airport_id]: true }));
        await upsertBacklog({
          provider_id: quote.provider_id,
          airport_id,
          expires_at: addDays(new Date(), 10),
          next_flight_at: null,
          status,
          starts_at: new Date(),
        });
      } finally {
        setBacklogsForAirportsLoading((prev) => ({ ...prev, [airport_id]: false }));
        const { data } = await refetch();
        setBacklogData(data);
      }
    },
    [quote.provider_id, refetch, upsertBacklog],
  );

  return (
    <SimplePopoverInput displayValue={<LabeledText label="Backlog" value={display} />} minW="150px">
      <LabeledText label="Backlog" value={display} />
      <LabeledText label="Customer Sees" value={getBacklogStatus(backlogData?.backlogs) || '-'} />

      <Stack gap="4" w="full">
        {airports.map((airport) => {
          const backlogs = backlogData?.backlogs.filter((backlog) => backlog.airport_id === airport);
          return (
            <Stack key={airport} w="full">
              <Text>{airport}</Text>
              {backlogs?.map((backlog) => <IndividualBacklog key={backlog.id} backlog={backlog} refetch={refetch} />)}
              <Show if={!backlogs?.length}>
                <Menu>
                  <MenuButton
                    as={Button}
                    w="fit-content"
                    size="xs"
                    leftIcon={<HiOutlinePlusCircle />}
                    isLoading={backlogsForAirportsLoading[airport]}
                  >
                    Add Backlog
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => addBacklog(airport, BacklogStatus.AtRisk)}>At Risk</MenuItem>
                    <MenuItem onClick={() => addBacklog(airport, BacklogStatus.Backlogged)}>Backlogged</MenuItem>
                  </MenuList>
                </Menu>
              </Show>
            </Stack>
          );
        })}
      </Stack>
    </SimplePopoverInput>
  );
};
